import React, { useState, useEffect, useRef } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircle, X, Send, Sparkles, Minimize2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const Copilot = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const location = useLocation();

  // Get context based on current page
  const getPageContext = () => {
    const path = location.pathname;
    const contextMap = {
      '/': 'You are on the Dashboard. I can help you navigate, understand statistics, or submit queries.',
      '/query': 'You are on the Query Submit page. I can help you formulate queries or explain how to use this feature.',
      '/resolutions': 'You are on the Knowledge Hub (Resolution Management). I can help you create, edit, or search resolutions.',
      '/teams': 'You are on Team Management. I can help you create teams or assign members.',
      '/users': 'You are on User Management. I can help you manage user roles and permissions.',
      '/analytics': 'You are on Analytics. I can help you understand query patterns and metrics.',
      '/floor-space': 'You are on Floor Space. I can help you use the live chat feature.',
      '/org-chart': 'You are on Org Chart. I can help you understand organizational structure.',
      '/sla': 'You are on SLA Management. I can help you create or manage SLA rules.',
      '/admin': 'You are in Admin. I can help explain Qflow instructions, retrieval rules, Web Mode, app connections, notifications, and access controls.',
      '/integrations': 'You are in Plugins. I can explain the available connections and what they can currently do.',
    };
    return contextMap[path] || 'I am Qflow Copilot. How can I help you today?';
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Keyboard shortcut: Ctrl/Cmd + K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: `👋 Hi ${user.name}! I'm your Qflow Copilot.\n\n${getPageContext()}\n\nAsk me anything about Qflow, or type a query to get instant help!`,
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  }, [isOpen, user.name]);

  const handleSend = async () => {
    if (!inputText.trim()) return;

    const userMessage = {
      role: 'user',
      content: inputText,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText('');
    setLoading(true);

    try {
      // Send to AI with context
      const context = `Current page: ${location.pathname}\nUser role: ${user.role}\n\nUser question: ${inputText}`;
      
      const res = await axiosInstance.post('/copilot/chat', {
        message: context,
        history: messages.slice(-5), // Last 5 messages for context
      });

      const assistantMessage = {
        role: 'assistant',
        content: res.data.response,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      toast.error('Failed to get response');
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Quick suggestions based on page
  const getQuickSuggestions = () => {
    const path = location.pathname;
    const suggestions = {
      '/': ['Show me recent queries', 'What can I do here?', 'Explain the dashboard'],
      '/query': ['How do I submit a query?', 'What happens after I submit?', 'Can you help me formulate a query?'],
      '/resolutions': ['How do I add a resolution?', 'Explain SLA fields', 'How do I upload forms?'],
      '/admin': ['Explain Qflow instructions', 'How do retrieval rules work?', 'What can app integrations do?'],
      '/integrations': ['What can app integrations do?', 'How do I test a connection?', 'How are connections secured?'],
    };
    return suggestions[path] || ['How can Qflow help me?', 'Explain my role', 'What features are available?'];
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 inline-flex h-10 w-10 items-center justify-center bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-full shadow-lg transition-colors duration-200 z-40 group"
        data-testid="copilot-open-btn"
        title="Open Copilot (Ctrl+K)"
      >
        <Sparkles size={18} />
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-semibold">
          AI
        </span>
      </button>
    );
  }

  return (
    <div
      className={`fixed ${isMinimized ? 'bottom-6 right-6' : 'bottom-0 right-0 md:bottom-6 md:right-6'} z-50 transition-all duration-300`}
      data-testid="copilot-panel"
    >
      <div
        className={`bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col ${
          isMinimized ? 'w-80 h-16' : 'w-full md:w-96 h-screen md:h-[600px]'
        }`}
        style={{ maxHeight: isMinimized ? '64px' : '90vh' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Sparkles size={20} />
            <h3 className="font-semibold">Qflow Copilot</h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="hover:bg-white/20 rounded p-1"
              data-testid="copilot-minimize-btn"
            >
              <Minimize2 size={18} />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 rounded p-1"
              data-testid="copilot-close-btn"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" data-testid="copilot-messages">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`copilot-message-${idx}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-800'
                    }`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="flex items-center space-x-2 mb-2">
                        <Sparkles size={14} className="text-purple-600" />
                        <span className="text-xs font-semibold text-purple-600">Qflow</span>
                      </div>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Suggestions */}
            {messages.length <= 1 && (
              <div className="px-4 py-2 bg-white border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Quick suggestions:</p>
                <div className="flex flex-wrap gap-2">
                  {getQuickSuggestions().map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInputText(suggestion)}
                      className="text-xs px-3 py-1 bg-purple-50 text-purple-700 rounded-full hover:bg-purple-100 transition-colors"
                      data-testid={`suggestion-${idx}`}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 bg-white rounded-b-lg border-t border-gray-200">
              <div className="flex space-x-2">
                <Input
                  data-testid="copilot-input"
                  placeholder="Ask me anything..."
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  className="flex-1"
                />
                <Button
                  onClick={handleSend}
                  disabled={loading || !inputText.trim()}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  data-testid="copilot-send-btn"
                >
                  <Send size={18} />
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-2 text-center">Press Ctrl+K to open/close</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Copilot;
